import React, { useState } from "react";
import { Info, ChevronDown } from "lucide-react";

/**
 * Collapsible callout explaining the interpretive-coding methodology.
 * Title is always visible; the detail expands on click. Defaults to
 * collapsed so it does not dominate the page.
 */
export default function MethodologyNote({
  compact = false,
  defaultOpen = false,
  testid,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = (testid || "methodology-note") + "-panel";
  return (
    <aside
      className="border-l-4 border-[#2F4F4F] bg-[#F8FAF9]"
      data-testid={testid || "methodology-note"}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className={
          "w-full flex items-center gap-3 text-left px-4 sm:px-5 focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/30 " +
          (compact ? "py-3" : "py-4")
        }
        data-testid={(testid || "methodology-note") + "-toggle"}
      >
        <Info className="h-4 w-4 text-[#2F4F4F] flex-shrink-0" />
        <span className="flex-1 text-sm font-semibold text-gray-900">
          Dual-layer reporting · Explicit vs. interpretive
        </span>
        <span className="text-[11px] font-mono uppercase tracking-wider text-[#2F4F4F] hidden sm:inline">
          {open ? "Hide" : "Read more"}
        </span>
        <ChevronDown
          className={
            "h-4 w-4 text-[#2F4F4F] transition-transform " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open ? (
        <div
          id={panelId}
          className={
            "pl-11 pr-4 sm:pr-5 text-sm text-gray-800 leading-relaxed " +
            (compact ? "pb-3" : "pb-5")
          }
          data-testid={(testid || "methodology-note") + "-content"}
        >
          <p>
            Workshops are deliberative exercises, not surveys. Participants
            express preferences through decisions, trade-offs and
            configurations. Where appropriate, this dashboard reports two
            layers:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <span className="font-medium">Explicit</span> — literal
              statements recorded in the report (conservative).
            </li>
            <li>
              <span className="font-medium">Including implicit</span> —
              preferences inferred from the configurations, scales and
              trade-offs participants chose (behavioural signal).
            </li>
          </ul>
          {!compact && (
            <p className="mt-2 text-xs text-gray-600">
              A workshop limited by landscape sensitivity or planning context
              but engaging constructively is classified as{" "}
              <em>supportive, constrained by context</em> — not as resistant.
              Original conservative figures are always preserved alongside.
            </p>
          )}
        </div>
      ) : null}
    </aside>
  );
}
